import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useApplicationContext } from 'shared/map-search/containers/ApplicationContext';
import { ExpSinglePinLogic } from 'shared/map-search/experiments/declarations';
import { TMapBounds } from 'shared/map-search/types/mapBounds';
import { IApplicationState } from 'shared/map-search/types/redux';

import { useFeaturesContext } from '../context';
import { isResultablePrecision } from './isResultablePrecision';

interface IResultabilityManagerProps {
  onResultabilityChange({ isResultable, isClusterable }: { isResultable: boolean; isClusterable: boolean }): void;
}

const DEFAULT_CLUSTER_PRECISION = 7;

/**
 * @description
 * Менеджер для управления флагом возможности открытия выдачи по тапу на пин
 */
export const ResultabilityManager: React.FC<IResultabilityManagerProps> = ({ onResultabilityChange }) => {
  const { config } = useApplicationContext();
  const { pointsLayer, hotspotsLayer } = useFeaturesContext();
  const isDesktop = useDeviceType() === 'desktop';
  const { precision } = useSelector<IApplicationState, TMapBounds>(state => state.mapBounds);
  // Эксперимент влияет не на отдельный визуальный блок, а на логику пинов/кластеров, поэтому
  // у него нет собственного «места» с разметкой. Хук дёргаем здесь намеренно: ResultabilityManager
  // монтируется на карте для обеих групп — так exposure уходит для всех пользователей.
  const isSinglePinExperimentActive = ExpSinglePinLogic.use() === 'B';
  const clusterPrecision =
    (isSinglePinExperimentActive && isDesktop && config.get<number>('mapSearchFrontend.clusterPrecision')) ||
    DEFAULT_CLUSTER_PRECISION;

  const isResultableByPrecision = isResultablePrecision({ config, isSinglePinExperimentActive, isDesktop, precision });
  const isClusterableByPrecision = precision >= clusterPrecision;

  React.useEffect(() => {
    pointsLayer.isResultable = isResultableByPrecision && isClusterableByPrecision;
    pointsLayer.layer.update();
    hotspotsLayer.isResultable = isResultableByPrecision && isClusterableByPrecision;
    hotspotsLayer.layer.update();

    onResultabilityChange({ isResultable: isResultableByPrecision, isClusterable: isClusterableByPrecision });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResultableByPrecision, isClusterableByPrecision]);

  return null;
};
